import { Platform } from 'react-native'

import {
  WEB_ANALYTICS_CONSENT_DENIED,
  WEB_ANALYTICS_CONSENT_GRANTED,
  WEB_ANALYTICS_CONSENT_META_STORAGE_KEY,
  WEB_ANALYTICS_CONSENT_STORAGE_KEY,
  WEB_ANALYTICS_CONSENT_VERSION,
  type WebAnalyticsConsentValue,
} from '../privacyConsent.constants'

interface WebAnalyticsConsentMeta {
  version: string
  decidedAt: string
}

export const WEB_ANALYTICS_CONSENT_UPDATED_EVENT = 'zporti:web-analytics-consent-updated'
const NATIVE_ANALYTICS_CONSENT_STORAGE_KEY = 'native_analytics_consent'
const listeners = new Set<() => void>()
let runtimeConsent: WebAnalyticsConsentValue =
  Platform.OS === 'web' ? null : WEB_ANALYTICS_CONSENT_GRANTED

function hasWebStorage(): boolean {
  return Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function isConsentValue(value: string | null): value is Exclude<WebAnalyticsConsentValue, null> {
  return value === WEB_ANALYTICS_CONSENT_GRANTED || value === WEB_ANALYTICS_CONSENT_DENIED
}

export function getWebAnalyticsConsent(): WebAnalyticsConsentValue {
  if (hasWebStorage()) {
    const value = window.localStorage.getItem(WEB_ANALYTICS_CONSENT_STORAGE_KEY)
    runtimeConsent = isConsentValue(value) ? value : null
  }
  return runtimeConsent
}

export function setWebAnalyticsConsent(value: Exclude<WebAnalyticsConsentValue, null>): void {
  runtimeConsent = value
  const meta: WebAnalyticsConsentMeta = {
    version: WEB_ANALYTICS_CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
  }
  if (hasWebStorage()) {
    window.localStorage.setItem(WEB_ANALYTICS_CONSENT_STORAGE_KEY, value)
    window.localStorage.setItem(WEB_ANALYTICS_CONSENT_META_STORAGE_KEY, JSON.stringify(meta))
    window.dispatchEvent(new Event(WEB_ANALYTICS_CONSENT_UPDATED_EVENT))
  } else {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default
    void AsyncStorage.setItem(NATIVE_ANALYTICS_CONSENT_STORAGE_KEY, value)
    void AsyncStorage.setItem(`${NATIVE_ANALYTICS_CONSENT_STORAGE_KEY}_meta`, JSON.stringify(meta))
  }
  listeners.forEach((listener) => listener())
}

export function subscribeAnalyticsConsent(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export async function hydrateNativeAnalyticsConsent(): Promise<void> {
  if (Platform.OS === 'web') return
  try {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default
    const value = await AsyncStorage.getItem(NATIVE_ANALYTICS_CONSENT_STORAGE_KEY)
    if (isConsentValue(value)) {
      runtimeConsent = value
      listeners.forEach((listener) => listener())
    }
  } catch {
    // ignore hydration errors; keep safe runtime default
  }
}
